/* Copyright (c) 2025 Xperi. All Rights Reserved. */

import React, { FunctionComponent } from 'react';

import { RecordingAvailability, RecordingStatus } from 'client-code/previews';

import { PreviewComponentAssets } from '../../../core/usecases';

interface IStatusMessageProps {
    entitlementAndAvailabilityStatus?: string;
    status?: string;
    isKeepForeverRecording?: boolean;
    showType?: string;
    isPartialRecording?: boolean;
    startTime?: string;
    endTime?: string;
    duration?: number;
    recordingAvailability?: RecordingAvailability;
}

/**
 * Detail mode: Part 0
 * For VOD:
 *  VOD Status Message
 * Otherwise:
 *  Air date & time
 *  Channel
 *  Status Message
 * When the episode is PPV, and purchased, the status message is:
 *  "To cancel this PPV _RENT _OR _BUY_NOUN, call CUSTOMER_SUPPORT_NAME at
 *  PHONE _CUSTOMER _SUPPORT _NUMBER."
 *
 * Notes:
 * - The status message is currently only implemented on streamers. It is desired for STBs,
 * but currently futured.
 * - Air date & time, channel, and (PPV) Status message don't apply within the VOD walled
 * garden.
 * @returns
 */
const StatusMessage: FunctionComponent<IStatusMessageProps> = ({
    entitlementAndAvailabilityStatus,
    status,
    isKeepForeverRecording,
    showType,
    isPartialRecording,
    startTime,
    endTime,
    duration,
    recordingAvailability,
}) => {
    const oPreviewAssets: PreviewComponentAssets = new PreviewComponentAssets();
    if (!entitlementAndAvailabilityStatus && !status && !isPartialRecording) return null;

    let assets = { stringAsset: '', imageAssetUrl: '' };

    // Use Cases for Recording status messages
    // Use Case 1: <recording now icon> This <show type> is currently recording
    assets = oPreviewAssets.getRecordingStatusAssets(status, showType, isKeepForeverRecording);

    // Use Case 2: <partial icon> Partial recording <start time> - <end time> | <duration>
    if (
        status === RecordingStatus.CompleteRecording &&
        isPartialRecording &&
        startTime &&
        endTime &&
        duration
    ) {
        assets = oPreviewAssets.getPartialRecordingStatusAssets(startTime, endTime, duration);
    }

    // Use Case 3: <not recordable cell icon> Channel does not allow recording.
    if (recordingAvailability && recordingAvailability !== RecordingAvailability.RecordingAllowed) {
        assets = oPreviewAssets.getRecordingAvailabilityAssets(recordingAvailability);
    }

    // Use Cases for VOD status messages
    if (entitlementAndAvailabilityStatus) {
        assets.stringAsset = entitlementAndAvailabilityStatus;
    }

    const imageSrc = assets.imageAssetUrl;

    return (
        <React.Fragment>
            <div className="uikit status-message">
                {imageSrc ? <img src={imageSrc} /> : null} <span>{assets.stringAsset}</span>
            </div>
        </React.Fragment>
    );
};

export default StatusMessage;
